import { ChevronLeft, ChevronRight } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
)
Pagination.displayName = 'Pagination'

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex flex-row items-center gap-0', className)} {...props} />
  )
)
PaginationContent.displayName = 'PaginationContent'

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
))
PaginationItem.displayName = 'PaginationItem'

type PaginationLinkProps = {
  isActive?: boolean
  disabled?: boolean
} & React.ComponentProps<'a'>

const paginationNumberClassName = ({
  isActive,
  disabled,
}: {
  isActive?: boolean
  disabled?: boolean
}) =>
  cn(
    'inline-flex h-10 w-10 items-center justify-center rounded-full text-sm outline-none transition-colors',
    'text-[#1f1f1f] font-normal',
    'hover:border hover:border-[#e0e0e0] hover:bg-white',
    'active:border active:border-[#e0e0e0] active:bg-[#f4f4f4]',
    isActive && 'border border-[#e0e0e0] bg-white font-medium text-[#1f1f1f]',
    disabled && 'pointer-events-none text-[#cfcfcf] hover:border-transparent hover:bg-transparent'
  )

const PaginationLink = ({ className, isActive, disabled, ...props }: PaginationLinkProps) => (
  <a
    aria-current={isActive ? 'page' : undefined}
    aria-disabled={disabled || undefined}
    className={cn(paginationNumberClassName({ isActive, disabled }), className)}
    {...props}
  />
)
PaginationLink.displayName = 'PaginationLink'

const PaginationPrevious = ({ className, disabled, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    disabled={disabled}
    className={cn('text-[#1f1f1f]', disabled && 'text-[#cfcfcf]', className)}
    {...props}
  >
    <ChevronLeft className="h-5 w-5" />
    <span className="sr-only">Previous</span>
  </PaginationLink>
)
PaginationPrevious.displayName = 'PaginationPrevious'

const PaginationNext = ({ className, disabled, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    disabled={disabled}
    className={cn('text-[#1f1f1f]', disabled && 'text-[#cfcfcf]', className)}
    {...props}
  >
    <ChevronRight className="h-5 w-5" />
    <span className="sr-only">Next</span>
  </PaginationLink>
)
PaginationNext.displayName = 'PaginationNext'

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn(
      'inline-flex h-10 w-10 items-center justify-center rounded-full text-sm font-normal text-[#adadad]',
      className
    )}
    {...props}
  >
    ...
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = 'PaginationEllipsis'

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
}
